import React from "react";
import { ChartBarIcon } from "@heroicons/react/24/outline";

interface EmptyStateProps {
  message: string;
  variant?: "info" | "warning" | "error";
  icon?: React.ComponentType<{ className?: string }>;
  description?: string;
  size?: "xs" | "sm" | "md" | "lg";
  /** Label for optional action button */
  actionLabel?: string;
  /** Callback when action button is clicked */
  onAction?: () => void;
}

/**
 * A component for displaying empty states when there's no data to show.
 * Provides a clean, modern design with optional icon, description, and action button.
 */
export function EmptyState({
  message,
  variant = "info",
  icon: Icon = ChartBarIcon,
  description,
  size = "md",
  actionLabel,
  onAction,
}: EmptyStateProps) {
  const sizeClasses = {
    xs: "py-4",
    sm: "py-8",
    md: "py-12",
    lg: "py-16",
  };

  const iconSizes = {
    xs: "w-8 h-8",
    sm: "w-12 h-12",
    md: "w-16 h-16",
    lg: "w-20 h-20",
  };

  const titleSizes = {
    xs: "text-sm",
    sm: "text-base",
    md: "text-lg",
    lg: "text-xl",
  };

  const variantStyles = {
    info: "text-muted-foreground",
    warning: "text-warning",
    error: "text-destructive",
  };

  const showIcon = size !== "xs";

  return (
    <div
      className={`flex flex-col items-center justify-center ${sizeClasses[size]} px-4`}
    >
      {showIcon && (
        <Icon
          className={`${iconSizes[size]} ${variantStyles[variant]} mb-4`}
        />
      )}
      <h3
        className={`${titleSizes[size]} font-semibold mb-2 ${
          size === "xs" ? "text-muted-foreground" : "text-foreground"
        }`}
      >
        {message}
      </h3>
      {description && (
        <p className="text-sm text-muted-foreground text-center max-w-md mb-2">
          {description}
        </p>
      )}
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          className="text-sm text-primary hover:text-primary/80 font-medium"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

